import { z } from 'zod';
import {
  isCanonicalRecipientPhone,
  normalizePhoneForStorage,
} from '../utils/phone';

// Verbatim from `crm.tour_operator_type_enum` in
// `packages/db/src/schema/index.ts:87-90`.
export const tourOperatorTypeValues = ['Driver', 'Guide'] as const;
export const tourOperatorTypeSchema = z.enum(tourOperatorTypeValues);
export type TourOperatorType = z.infer<typeof tourOperatorTypeSchema>;

// Verbatim from `crm.tour_operator_frequency_enum` (schema:79-82).
export const tourOperatorFrequencyValues = [
  'Frequently',
  'Occasionally',
  'Rare',
] as const;
export const tourOperatorFrequencySchema = z.enum(tourOperatorFrequencyValues);
export type TourOperatorFrequency = z.infer<typeof tourOperatorFrequencySchema>;

// Verbatim from `crm.tour_operator_receive_message_enum` (schema:83-86).
export const tourOperatorReceiveMessageValues = [
  'allowed',
  'only_preparation',
  'not_allowed',
] as const;
export const tourOperatorReceiveMessageSchema = z.enum(
  tourOperatorReceiveMessageValues
);
export type TourOperatorReceiveMessage = z.infer<
  typeof tourOperatorReceiveMessageSchema
>;

// Embedded `company` on the read shape — populated via LEFT JOIN on
// `crm.company`. `null` when the FK row is missing (shouldn't happen
// because companyId is NOT NULL on the table, but the join is left
// for defensive reasons mirroring `companies.service.ts`).
const tourOperatorCompanySchema = z
  .object({
    id: z.uuid(),
    name: z.string().nullable(),
  })
  .nullable();

export const tourOperatorSchema = z.object({
  id: z.uuid(),
  fullName: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  companyId: z.uuid(),
  organizationId: z.uuid(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  type: tourOperatorTypeSchema,
  frequency: tourOperatorFrequencySchema.nullable(),
  // `crm.tour_operator.rating` is Postgres `numeric` — Drizzle returns
  // it as `string`. The service coerces to `number` before mapping; if
  // the row has no rating we surface `null`. Range matches legacy
  // 1..5 (cap enforced on create/update).
  rating: z.number().min(1).max(5).nullable(),
  languages: z.array(z.string()).nullable(),
  specials: z.array(z.string()).nullable(),
  notes: z.string().nullable(),
  receiveMessage: tourOperatorReceiveMessageSchema,
  company: tourOperatorCompanySchema,
  createdOn: z.string(),
  modifiedOn: z.string().nullable(),
});

export type TourOperator = z.infer<typeof tourOperatorSchema>;

export const tourOperatorSortFields = [
  'fullName',
  'rating',
  'city',
  'country',
] as const;
export type TourOperatorSortField = (typeof tourOperatorSortFields)[number];

export const findTourOperatorsInput = z.object({
  fullName: z.string().optional(),
  type: z.array(tourOperatorTypeSchema).optional(),
  city: z.array(z.string()).optional(),
  rating: z.array(z.number().int().min(1).max(5)).optional(),
  specials: z.array(z.string()).optional(),
  country: z.array(z.string()).optional(),
  companyIds: z.array(z.uuid()).optional(),
  page: z.number().int().min(0).default(0),
  pageSize: z.number().int().min(1).max(100).default(20),
  sortBy: z.enum(tourOperatorSortFields).default('fullName'),
  sortOrder: z.enum(['asc', 'desc']).default('asc'),
});
export type FindTourOperatorsInput = z.infer<typeof findTourOperatorsInput>;

/**
 * Write-side phone: strips whitespace/bidi so nothing but `+<country><digits>`
 * ever reaches the column, then requires canonical E.164.
 *
 * Blank stays allowed (`null`) — an operator with no number is legitimate, it
 * simply cannot be messaged. What is NOT allowed is a bare national number:
 * Twilio used to resolve those by inferring the country from the sender, so
 * they looked fine until the send boundary started rejecting them (XT-512).
 * The read schema stays a loose `string` so pre-existing rows remain readable.
 */
export const tourOperatorPhoneInput = z
  .string()
  .nullable()
  .optional()
  // `undefined` must stay `undefined`: the update path keys off
  // `patch.phone !== undefined`, so collapsing absent -> null here would wipe
  // the number on every partial update that does not touch it.
  .transform((value) =>
    value === undefined ? undefined : normalizePhoneForStorage(value)
  )
  .refine((value) => value == null || isCanonicalRecipientPhone(value), {
    message:
      'Phone must include the country code, e.g. +351912345678. A national number on its own cannot be messaged.',
  });

// Legacy parity: 8 editable fields. `companyId` is required — the form
// always picks an Account; no "Default supplier" server fallback.
export const createTourOperatorInput = z.object({
  fullName: z.string().min(1),
  type: tourOperatorTypeSchema,
  receiveMessage: tourOperatorReceiveMessageSchema.default('only_preparation'),
  companyId: z.uuid(),
  phone: tourOperatorPhoneInput,
  email: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  rating: z.number().int().min(1).max(5).nullable().optional(),
});
export type CreateTourOperatorInput = z.infer<typeof createTourOperatorInput>;

export const updateTourOperatorInput = createTourOperatorInput
  .partial()
  .extend({ id: z.uuid() });
export type UpdateTourOperatorInput = z.infer<typeof updateTourOperatorInput>;

export const filterCountsInput = z.object({
  type: z.array(tourOperatorTypeSchema).optional(),
});
export type FilterCountsInput = z.infer<typeof filterCountsInput>;

export const countriesCitiesInput = filterCountsInput.default({});
export type CountriesCitiesInput = z.infer<typeof countriesCitiesInput>;

export const filterCountsResult = z.object({
  cityCount: z.array(
    z.object({ city: z.string(), count: z.number().int().nonnegative() })
  ),
  ratingCount: z.array(
    z.object({
      rating: z.number().int().min(1).max(5),
      count: z.number().int().nonnegative(),
    })
  ),
  specialsCount: z.array(
    z.object({ special: z.string(), count: z.number().int().nonnegative() })
  ),
});
export type FilterCountsResult = z.infer<typeof filterCountsResult>;

export const countriesCitiesResult = z.object({
  countries: z.array(z.string()),
  cities: z.array(z.string()),
});
export type CountriesCitiesResult = z.infer<typeof countriesCitiesResult>;
